/**
 * Directives read scope — whose records the current session may see.
 *
 * Scope is a property of the ACTOR, never of the session mode:
 *
 *   directive_read         (52) — may read directives, for assigned patients
 *   directive_all_patients (65) — widens that to every patient in the org
 *
 * Three outcomes, first match wins:
 *
 *   holds 65               -> ORG       (administrators; not granted by break-glass)
 *   is a patient           -> SELF      (their own record; also family/contacts,
 *                                        who have no patient row and so resolve
 *                                        to NONE — see the qa360family note)
 *   otherwise (a caregiver)-> ASSIGNED  (their panel, possibly empty)
 *
 * ASSIGNED resolves from the caregiver's owner-scoped "Patients" group, the
 * same structure that already scopes /patients, HomeGrid, messaging and
 * scheduling. An empty panel is a legitimate ASSIGNED result, not NONE: the
 * page renders an empty-state explaining that nothing is assigned yet, which is
 * a different message from "you may not look".
 *
 * Whether the caller holds 52 at all is a separate question the pages already
 * ask (`canRead`); this module only answers "whose".
 *
 * Two things are deliberately NOT consulted, both of which were tried and were
 * wrong:
 *
 *   The open-chart flag (`viewingPatientChart`) — only means a staff member
 *   has stepped into a chart. It says nothing about who the person behind the
 *   session is, and keying scope off it is what let a directly-logged-in
 *   patient (no chart open, no `manager`) read the entire org roster.
 *
 *   `allData.administrator` — pacmny-be's Postgres `ZV_user_admin`. It does
 *   not track role: on the local stack `dirreadonly` and `qa360patient` are
 *   both account_type Patient with `administrator === true`, while
 *   `annettehaven` is a Patient with `administrator === false`.
 *
 * An interim version of this module resolved scope from keyon's
 * `account_user.account_type` via a GetAccountType round-trip. That was a
 * documented stand-in for privilege 65 before it existed. It is gone: role is
 * not permission, it could not be tuned per-org, and it cost a request on
 * every page load.
 *
 * DEPLOY ORDER MATTERS. This reads privilege 65, created by
 * db/migrations/mysql/20260908160645_ref_directive_all_patients_privilege.sql and
 * granted to existing orgs by 20260908160722_data_directive_all_patients_backfill.sql.
 * Ship the migrations first. On an environment where they have not run, nobody
 * holds 65 and every clinician falls to ASSIGNED — correct for caregivers, but
 * administrators lose the org roster until the backfill lands.
 *
 * This is the UX boundary; keyon enforces the same three outcomes independently
 * in ProcessData::ResolveDirectiveReadScope, and the read endpoints now require
 * an actor. Disagreement between the two is a bug, but the server wins.
 */

import { PRIVILEGES } from "@/constants/privileges";
import { getDirectiveAccountNumber, isViewingPatientChart } from "@/utils/directivesSession";

/** Scope kinds. `none` is the fail-closed outcome, never a synonym for `self`. */
export const SCOPE = Object.freeze({
  ORG: "org",
  ASSIGNED: "assigned",
  SELF: "self",
  NONE: "none",
});

/**
 * Resolve the current session's directives read scope.
 *
 * @param {object} allData Vuex `login/getAllData` snapshot
 * @param {object} privilegesStore loaded usePrivilegesStore() instance
 * @returns {{kind: string, reason?: string}}
 */
export function resolveDirectiveScope(allData, privilegesStore) {
  const data = allData || {};
  if (!getDirectiveAccountNumber(data)) {
    return { kind: SCOPE.NONE, reason: "no_account" };
  }
  if (!privilegesStore || typeof privilegesStore.effectiveGranted !== "function") {
    return { kind: SCOPE.NONE, reason: "no_privileges" };
  }
  if (privilegesStore.effectiveGranted(PRIVILEGES.DIRECTIVE_ALL_PATIENTS)) {
    return { kind: SCOPE.ORG };
  }
  // SELF is tentative here: it holds for anyone whose session resolves to a
  // patient record. A caregiver has none, and the page downgrades them to
  // ASSIGNED once the pid lookup comes back empty — that lookup is async, so it
  // cannot happen in this synchronous resolver.
  //
  // `tentative` says so out loud. Callers that merely FILTER may treat this as
  // SELF, because the server scopes them either way. Callers that decide WHOSE
  // IDENTITY to render must not — see canUseSessionAsPatientIdentity.
  return { kind: SCOPE.SELF, tentative: true };
}

/**
 * Whether `allData.patient` may be rendered as the subject of the chart on
 * screen — the name, DOB and MRN in the detail banner.
 *
 * This is deliberately stricter than "is this session self-scoped". A caregiver
 * resolves to a TENTATIVE self scope (above) and only becomes ASSIGNED after an
 * async pid lookup returns nothing. Between those two points `allData.patient`
 * is the logged-in caregiver, not the patient whose chart is open, so treating
 * self-scope alone as permission paints one real person's name and DOB over
 * another person's directives until the lookup lands — a misidentification, not
 * a flicker, and visible for the length of two awaited round trips.
 *
 * Two ways to earn it, and only these:
 *   - a chart is open, so `allData.patient` IS by definition its subject;
 *   - the session resolved its OWN patients.pid, which a caregiver never does.
 *
 * Anything else withholds, and the banner shows its loading state until the
 * real patient arrives.
 */
export function canUseSessionAsPatientIdentity({
  viewingPatientChart = false,
  scope = null,
  resolvedPatientPid = "",
} = {}) {
  if (viewingPatientChart) return true;
  if (!scope || scope.kind !== SCOPE.SELF) return false;
  return !!resolvedPatientPid;
}

/**
 * Whether the gate's two inputs are actually present yet.
 *
 * The directives gate is two questions, not one: WHOSE records (this module's
 * scope) and MAY THEY READ AT ALL (`directive_read`, from the privileges
 * store). A page is blocked when either fails — so both have to have been
 * ANSWERED before a refusal means anything.
 *
 * The predecessor of this function inspected only the scope, and that is the
 * whole of the bug it replaces. After a hard reload the privileges store is
 * empty but present, so `resolveDirectiveScope` sees a live store object and
 * returns a tentative SELF rather than `no_privileges` — a scope-only check
 * finds nothing wrong, while `canRead` is false and the page is blocked
 * anyway. The refusal then froze, and moments later the privileges landed,
 * `accessBlocked` flipped to false, and the page rendered its loading state
 * with nothing left to run: a permanent spinner over an empty roster.
 *
 * `loadedKey`, not `assignments.length`: an actor who legitimately holds no
 * privileges loads successfully with an empty array, and that is a real answer
 * — refused, and settled. Only a load that has not happened is a race.
 */
export function directiveGateInputsReady(allData, privilegesStore) {
  if (!getDirectiveAccountNumber(allData || {})) return false;
  return !!(privilegesStore && privilegesStore.loadedKey);
}

/**
 * Whether a page that has not settled should resolve its gate again.
 *
 * `created()` runs once. After a hard reload the app-level FSM resets and
 * neither Vuex `login/getAllData` nor the privileges store has rehydrated by
 * the time it runs, so the page fails closed and nothing re-runs — a caregiver
 * stares at an empty roster with their patients still assigned.
 *
 * The rule is the one this module can state honestly: A REFUSAL IS SETTLED
 * ONLY IF THE INPUTS WERE READY WHEN IT WAS COMPUTED. A genuine refusal —
 * privileges loaded, `directive_read` absent — is therefore never retried,
 * however often the session object changes underneath it; a refusal computed
 * against a session that had not arrived is retried once it has.
 *
 * `initializing` keeps `created()` and the watcher from both loading: the pass
 * in flight re-reads both inputs after each `await`, so it will pick up
 * whatever has landed on its own.
 */
export function shouldRetryDirectiveInit({
  initialized = false,
  initializing = false,
  allData = null,
  privilegesStore = null,
} = {}) {
  if (initialized || initializing) return false;
  return directiveGateInputsReady(allData, privilegesStore);
}

/**
 * Whether an ORG-WIDE directives aggregate may be shown to this session — the
 * nav badge's "N patients awaiting sign-off" count.
 *
 * Two reasons to withhold it, and they are different:
 *   - self-scoped session: the number is not theirs to see. A patient, family
 *     member or contact would otherwise learn how many patients across the
 *     whole organization are awaiting sign-off.
 *   - a patient's chart is open: the nav is scoped to that one patient there,
 *     so an org-wide count next to their name reads as if it were about them.
 *
 * Lives here rather than in the component so the rule sits with the other
 * scope rules and is testable — a spec cannot import a `.vue` file in this
 * repo today (no `ts-jest`, see the failing component suites).
 */
export function canSeeOrgDirectiveCounts(allData, privilegesStore) {
  if (isViewingPatientChart(allData)) return false;
  return resolveDirectiveScope(allData, privilegesStore).kind === SCOPE.ORG;
}
